import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { NgForm, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { I18NextPipe } from 'angular-i18next';
import { Observable, Subject, filter, finalize, map, switchMap, takeUntil } from 'rxjs';

import { ToastOptions } from '@hdms/shared/common-types';
import { RecaptchaService } from '@hdms/shared/data-access';
import { UserAccount, UserAccountData } from '@hdms/shared/page-frame/types';
import { TabService, ToastMessagesService } from '@hdms/shared/ui-core';
import { AnalyticsService, BreakpointService, ScrollIntoViewBlock } from '@hdms/shared/util-core';

import { MenuStateService } from '../../../../../services';
import { ContactBodyLoaderService } from '../../contact-body-loader.service';

export const ContactFormType = {
  CALL_BACK: 'callBack',
  SEND_MESSAGE: 'sendMessage'
};

export const ContactFormAction = {
  CALL_BACK: 'CallBack',
  CALL_US: 'CallUs',
  CHAT: 'Chat',
  SEND_MESSAGE: 'Message',
  SELF_SERVICE: 'SelfService',
  STORE: 'FindHiltiStore'
};

export const ContactFormLabel = {
  OPEN: 'Open|Overlay',
  CLOSE: 'Close|Overlay',
  SUBMIT: 'Submit|Overlay'
};

export const ContactFormCategory = {
  CONTACTUS: 'ContactUs'
};

@Directive()
export abstract class ContactFormComponentAbstract implements OnInit, AfterViewInit, OnDestroy {
  @Input() userData: UserAccountData;

  readonly rows$ = inject(BreakpointService).isMobileView$.pipe(map(isMobile => (isMobile ? 3 : 2)));

  formGroup: UntypedFormGroup;

  protected readonly cdr = inject(ChangeDetectorRef);
  protected readonly formBuilder = inject(UntypedFormBuilder);
  protected readonly menuState = inject(MenuStateService);
  protected readonly toastMessagesService = inject(ToastMessagesService);
  protected readonly i18NextPipe = inject(I18NextPipe);
  protected readonly recaptchaService = inject(RecaptchaService);
  protected readonly analyticsService = inject(AnalyticsService);
  protected readonly loaderService = inject(ContactBodyLoaderService);
  private readonly tabService = inject(TabService);

  @ViewChild('form')
  private readonly form: NgForm;
  private readonly unsubscribe$ = new Subject<void>();

  private initialValues: Record<string, string>;
  private readonly toastMessageOptions: ToastOptions = {
    tapToDismiss: true,
    autoClose: true,
    timeout: 4000,
    showCloseButton: true
  };

  displayErrorMessage = false;
  readonly errorMsgAlignment = ScrollIntoViewBlock.NEAREST;

  ngOnInit(): void {
    this.createForm();
    this.updateInitialValues();
  }

  ngAfterViewInit(): void {
    this.tabService.change
      .pipe(
        filter(_ => this.form && this.formGroup?.touched),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(_ => this.form.resetForm(this.initialValues));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  abstract onSubmit(): void;

  protected abstract createForm(): void;

  protected updateInitialValues(): void {
    if (this.formGroup) {
      this.initialValues = this.formGroup.value;
    }
  }

  protected getAccount(): UserAccount {
    return this.userData?.selectedAccount || ({} as UserAccount);
  }

  protected getUserFullName(): string {
    const { contact } = this.getAccount();
    return this.userData?.isRegistered && contact ? `${contact?.firstName} ${contact?.lastName}` : '';
  }

  protected handleFormSubmit<TInput, TResponse>(
    formType: string,
    formAction: string,
    input: TInput,
    callbackFn: (input: TInput) => Observable<TResponse>
  ): void {
    let observable$: Observable<TResponse>;

    this.displayErrorMessage = false;
    this.loaderService.setSubmitButtonEnabled(false);

    if (this.userData?.isRegistered) {
      // no Recaptcha token needed
      observable$ = callbackFn(input);
    } else {
      observable$ = this.recaptchaService.generateV3Token$().pipe(switchMap(token => callbackFn({ ...input, token })));
    }

    observable$
      .pipe(
        finalize(() => {
          this.loaderService.setSubmitButtonEnabled(true);
        })
      )
      .subscribe({
        next: () => this.handleSuccess(formType, formAction),
        error: () => this.handleError()
      });
  }

  protected handleError(): void {
    // display Danger micro-message
    this.displayErrorMessage = true;
    this.cdr.detectChanges();
  }

  private handleSuccess(formType: string, formAction: string): void {
    const translationPath = `header.contact.${formType}.successMsg`;
    this.toastMessagesService.success(
      this.i18NextPipe.transform(`${translationPath}.message`),
      this.i18NextPipe.transform(`${translationPath}.headline`),
      this.toastMessageOptions
    );
    this.analyticsService.sendEvent({
      category: ContactFormCategory.CONTACTUS,
      action: formAction,
      label: ContactFormLabel.SUBMIT
    });
    this.menuState.closeAll();
  }
}
